import { Alert, Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  TRACKER_EYEBROW_SX,
  TRACKER_SURFACE_SX,
} from '../../components/groups/guests/guestTableStyles';

/**
 * Why the group page could not show a group, as the page needs to tell it apart:
 *
 *  - `unavailable`: the API answered, and the answer was "not yours" (403) or "not
 *    here" (404). OrchestratorAPI answers 404 for a group the signed-in user does not
 *    own or co-host, so for the host this is usually "I am signed in as the other
 *    account". Asking again cannot change it.
 *  - `error`: anything else (a 500, a timeout, a dropped connection). Worth asking again.
 */
export type GroupLoadFailure = 'unavailable' | 'error';

const statusOf = (error: unknown): number | undefined => {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : undefined;
};

export function classifyGroupLoadError(error: unknown): GroupLoadFailure {
  const status = statusOf(error);
  return status === 403 || status === 404 ? 'unavailable' : 'error';
}

/**
 * Whether react-query should ask again. A 403/404 is the server's final answer; the
 * default (3 retries with backoff) held the page on a spinner for about seven seconds
 * before it would say so, which read as an endless spinner.
 */
export function shouldRetryGroupLoad(failureCount: number, error: unknown): boolean {
  return classifyGroupLoadError(error) === 'error' && failureCount < 1; // one quiet retry, then say so
}

const COPY: Record<GroupLoadFailure, { title: string; body: string }> = {
  unavailable: {
    title: "This group isn't available to this account",
    body:
      'It may belong to a different login, or it may have been removed. If you have more than one Tripsby account, '
      + 'use Logout and sign back in with the one that created or co-hosts this group.',
  },
  error: {
    title: "Couldn't load this group",
    body: "We couldn't reach Tripsby. Try again in a moment.",
  },
};

/**
 * The page's answer when the group query failed. Same flat card and hairline-ruled
 * eyebrow heading as the rest of Studio (guestTableStyles), with a way out in both cases.
 */
export default function GroupLoadState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const failure = classifyGroupLoadError(error);
  const { title, body } = COPY[failure];
  return (
    <Box component="section" aria-labelledby="group-load-state-heading" sx={{ ...TRACKER_SURFACE_SX, p: 3, maxWidth: 640 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography
          id="group-load-state-heading"
          component="h1"
          sx={{ ...TRACKER_EYEBROW_SX, color: 'var(--tsb-fg-muted)' }}
        >
          {failure === 'unavailable' ? 'Group unavailable' : 'Group not loaded'}
        </Typography>
        <Box aria-hidden="true" sx={{ flex: 1, height: '1px', bgcolor: 'var(--tsb-border-soft)' }} />
      </Box>
      <Typography component="h2" variant="h6" fontWeight={800}>{title}</Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>{body}</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
        {failure === 'error' ? <Button variant="contained" onClick={onRetry}>Try again</Button> : null}
        <Button component={RouterLink} to="/groups" variant={failure === 'error' ? 'outlined' : 'contained'}>Back to Groups</Button>
      </Box>
    </Box>
  );
}

/**
 * Shown ABOVE a page that did load, when a later background refetch (window focus, an
 * invalidation) failed. react-query keeps the last good `data` and sets `error`, so the page, and
 * any dialog open on it, must stay: the banner says what we are showing and offers a retry. A
 * 403/404 here means the group may have gone away from this account since it loaded.
 */
export function GroupRefreshBanner({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const gone = classifyGroupLoadError(error) === 'unavailable';
  return (
    <Alert
      severity="warning"
      sx={{ mb: 2, '@media print': { display: 'none' } }}
      action={<Button color="inherit" size="small" onClick={onRetry}>Retry</Button>}
    >
      {gone
        ? "This group may no longer be available to this account. Showing what we last loaded."
        : "Couldn't refresh this group. Showing what we last loaded."}
    </Alert>
  );
}
